/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import Icon from '../components/AppIcon';
import { initialWorkspaceState, workspaceReducer } from './windowManager';

const WorkspaceContext = createContext(null);
const WorkspaceWindowLifecycleContext = createContext(null);
const OPEN_EVENT = 'nova:workspace-open';

export function openWorkspaceWindow(descriptor) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: descriptor }));
}

export function useWorkspaceWindows() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error(
      'useWorkspaceWindows must be used inside WorkspaceWindowHost'
    );
  }
  return context;
}

export function useWorkspaceWindowLifecycle() {
  const context = useContext(WorkspaceWindowLifecycleContext);
  if (!context) {
    throw new Error(
      'useWorkspaceWindowLifecycle must be used inside a managed workspace window'
    );
  }
  return context;
}

export function useWorkspaceWindowUnsavedChanges(dirty, message) {
  const lifecycle = useWorkspaceWindowLifecycle();

  useEffect(() => {
    lifecycle.setDirty(dirty, message);
    return () => lifecycle.setDirty(false);
  }, [dirty, lifecycle, message]);
}

function getViewport(element) {
  return {
    width: Math.max(1, element?.clientWidth || window.innerWidth || 1),
    height: Math.max(1, element?.clientHeight || window.innerHeight || 1),
  };
}

export default function WorkspaceWindowHost({ children }) {
  const hostRef = useRef(null);
  const stateRef = useRef(initialWorkspaceState);
  const dirtyRef = useRef(new Map());
  const openersRef = useRef(new Map());
  const interactionRef = useRef(null);
  const [state, dispatch] = useReducer(workspaceReducer, initialWorkspaceState);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 768
  );

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const viewport = useCallback(() => getViewport(hostRef.current), []);

  const focusWindow = useCallback(id => {
    dispatch({ type: 'focus', id });
    requestAnimationFrame(() => {
      hostRef.current
        ?.querySelector(`[data-workspace-window-id="${id}"]`)
        ?.focus({ preventScroll: true });
    });
  }, []);

  const openWindow = useCallback(
    descriptor => {
      if (!descriptor?.id || !descriptor?.title || descriptor.content == null)
        return;
      if (!stateRef.current.windows.some(item => item.id === descriptor.id)) {
        const active = document.activeElement;
        if (active instanceof HTMLElement)
          openersRef.current.set(descriptor.id, active);
      }
      dispatch({ type: 'open', window: descriptor, viewport: viewport() });
    },
    [viewport]
  );

  const closeWindow = useCallback(
    id => {
      const dirty = dirtyRef.current.get(id);
      if (
        dirty?.dirty &&
        !window.confirm(
          dirty.message || 'Hay cambios sin guardar. ¿Deseas cerrar la ventana?'
        )
      ) {
        focusWindow(id);
        return;
      }

      dirtyRef.current.delete(id);
      dispatch({ type: 'close', id });

      requestAnimationFrame(() => {
        const opener = openersRef.current.get(id);
        openersRef.current.delete(id);
        opener?.focus?.({ preventScroll: true });
      });
    },
    [focusWindow]
  );

  const minimizeWindow = useCallback(
    id => dispatch({ type: 'minimize', id }),
    []
  );
  const maximizeWindow = useCallback(
    id => dispatch({ type: 'maximize', id, viewport: viewport() }),
    [viewport]
  );
  const restoreWindow = useCallback(
    id => dispatch({ type: 'restore', id, viewport: viewport() }),
    [viewport]
  );

  const setDirty = useCallback((id, dirty, message) => {
    if (!dirty) {
      dirtyRef.current.delete(id);
      return;
    }
    dirtyRef.current.set(id, { dirty: true, message });
  }, []);

  useEffect(() => {
    const onOpen = event => openWindow(event.detail);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [openWindow]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const update = () => {
      const nextMobile = window.innerWidth < 768;
      setIsMobile(nextMobile);
      dispatch({ type: 'reflow', viewport: viewport() });
    };

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(host);
    window.addEventListener('resize', update);
    update();

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [viewport]);

  useEffect(() => {
    const beforeUnload = event => {
      if (![...dirtyRef.current.values()].some(entry => entry.dirty)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  useEffect(() => {
    const onPointerMove = event => {
      const interaction = interactionRef.current;
      if (!interaction) return;

      const dx = event.clientX - interaction.startX;
      const dy = event.clientY - interaction.startY;

      if (interaction.type === 'move') {
        dispatch({
          type: 'move',
          id: interaction.id,
          x: interaction.bounds.x + dx,
          y: interaction.bounds.y + dy,
          viewport: viewport(),
        });
      } else {
        dispatch({
          type: 'resize',
          id: interaction.id,
          width: interaction.bounds.width + dx,
          height: interaction.bounds.height + dy,
          viewport: viewport(),
        });
      }
    };

    const onPointerUp = () => {
      interactionRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [viewport]);

  const startInteraction = useCallback(
    (event, managedWindow, type) => {
      if (isMobile || managedWindow.maximized) return;
      event.preventDefault();
      event.stopPropagation();
      focusWindow(managedWindow.id);
      interactionRef.current = {
        type,
        id: managedWindow.id,
        startX: event.clientX,
        startY: event.clientY,
        bounds: managedWindow.bounds,
      };
      document.body.style.userSelect = 'none';
      document.body.style.cursor = type === 'move' ? 'grabbing' : 'nwse-resize';
    },
    [focusWindow, isMobile]
  );

  const contextValue = useMemo(
    () => ({
      windows: state.windows,
      activeWindowId: state.activeWindowId,
      openWindow,
      closeWindow,
      focusWindow,
      minimizeWindow,
      maximizeWindow,
      restoreWindow,
    }),
    [
      state.windows,
      state.activeWindowId,
      openWindow,
      closeWindow,
      focusWindow,
      minimizeWindow,
      maximizeWindow,
      restoreWindow,
    ]
  );

  return (
    <WorkspaceContext.Provider value={contextValue}>
      <div
        ref={hostRef}
        className="cp07-workspace-host relative flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {children}

        <div className="pointer-events-none absolute inset-0 z-40">
          {state.windows.map(managedWindow => {
            if (managedWindow.minimized) return null;
            const mobileStyle = isMobile
              ? {
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: managedWindow.zIndex + 40,
                }
              : {
                  left: managedWindow.bounds.x,
                  top: managedWindow.bounds.y,
                  width: managedWindow.bounds.width,
                  height: managedWindow.bounds.height,
                  zIndex: managedWindow.zIndex + 40,
                };

            return (
              <section
                key={managedWindow.id}
                tabIndex={-1}
                data-workspace-window-id={managedWindow.id}
                className="cp07-managed-window pointer-events-auto absolute flex min-h-0 flex-col overflow-hidden outline-none md:rounded-[18px]"
                style={mobileStyle}
                onPointerDown={() => {
                  if (state.activeWindowId !== managedWindow.id) {
                    focusWindow(managedWindow.id);
                  }
                }}
                aria-label={managedWindow.title}
              >
                <header
                  className="cp07-window-titlebar flex h-9 shrink-0 items-center gap-2 px-2.5"
                  onPointerDown={event =>
                    startInteraction(event, managedWindow, 'move')
                  }
                  onDoubleClick={() =>
                    managedWindow.maximized
                      ? restoreWindow(managedWindow.id)
                      : maximizeWindow(managedWindow.id)
                  }
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-slate-800">
                      {managedWindow.title}
                    </p>
                  </div>
                  <div
                    className="flex items-center gap-1"
                    onPointerDown={event => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="rounded-md p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                      onClick={() => minimizeWindow(managedWindow.id)}
                      title="Minimizar"
                    >
                      <Icon name="Minus" size={14} />
                    </button>
                    {!isMobile && (
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                        onClick={() =>
                          managedWindow.maximized
                            ? restoreWindow(managedWindow.id)
                            : maximizeWindow(managedWindow.id)
                        }
                        title={
                          managedWindow.maximized ? 'Restaurar' : 'Maximizar'
                        }
                      >
                        <Icon
                          name={
                            managedWindow.maximized ? 'Minimize2' : 'Maximize2'
                          }
                          size={14}
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="rounded-md p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => closeWindow(managedWindow.id)}
                      title="Cerrar"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                </header>

                <WorkspaceWindowLifecycleContext.Provider
                  value={{
                    id: managedWindow.id,
                    setDirty: (dirty, message) =>
                      setDirty(managedWindow.id, dirty, message),
                  }}
                >
                  <div className="min-h-0 flex-1 overflow-auto">
                    {managedWindow.content}
                  </div>
                </WorkspaceWindowLifecycleContext.Provider>

                {!isMobile && !managedWindow.maximized && (
                  <button
                    type="button"
                    aria-label="Redimensionar ventana"
                    className="absolute bottom-0 right-0 h-5 w-5 cursor-nwse-resize"
                    onPointerDown={event =>
                      startInteraction(event, managedWindow, 'resize')
                    }
                  >
                    <span className="absolute bottom-1 right-1 h-2.5 w-2.5 border-b-2 border-r-2 border-slate-300" />
                  </button>
                )}
              </section>
            );
          })}
        </div>

        {state.windows.length > 0 && (
          <div className="cp07-taskbar pointer-events-auto absolute bottom-2 left-1/2 z-[100] flex max-w-[calc(100%-24px)] -translate-x-1/2 gap-1 overflow-x-auto rounded-2xl p-1.5">
            {state.windows.map(managedWindow => {
              const active =
                state.activeWindowId === managedWindow.id &&
                !managedWindow.minimized;
              return (
                <button
                  key={managedWindow.id}
                  type="button"
                  className={`max-w-48 truncate rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? 'cp07-task-active text-slate-950'
                      : 'bg-white/55 text-slate-700 hover:bg-white/85'
                  }`}
                  onClick={() =>
                    managedWindow.minimized
                      ? restoreWindow(managedWindow.id)
                      : focusWindow(managedWindow.id)
                  }
                  title={managedWindow.title}
                >
                  {managedWindow.title}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </WorkspaceContext.Provider>
  );
}
